import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ClipboardList, Printer, Calendar, RefreshCw, ChefHat, Package } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyOrdersPage() {
  const { language } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [orders, setOrders] = useState([]);
  const [aggregatedProducts, setAggregatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalOrders: 0, totalItems: 0, totalAmount: 0 });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales`);
      const allSales = response.data;
      
      // Filter sales for selected date
      const filteredSales = allSales.filter(sale => {
        const saleDate = sale.date.split('T')[0];
        return saleDate === selectedDate;
      });
      
      setOrders(filteredSales);
      
      // Aggregate products
      const productMap = {};
      let totalItems = 0;
      let totalAmount = 0;
      
      filteredSales.forEach(sale => {
        totalAmount += sale.total || 0;
        sale.items?.forEach(item => {
          const key = item.product_id || item.product_name;
          totalItems += item.quantity || 0;
          
          if (productMap[key]) {
            productMap[key].quantity += item.quantity || 0;
            productMap[key].total += (item.quantity || 0) * (item.unit_price || 0);
          } else {
            productMap[key] = {
              product_id: item.product_id,
              product_name: item.product_name,
              quantity: item.quantity || 0,
              unit_price: item.unit_price || 0,
              total: (item.quantity || 0) * (item.unit_price || 0)
            };
          }
        });
      });
      
      const aggregated = Object.values(productMap).sort((a, b) => b.quantity - a.quantity);
      setAggregatedProducts(aggregated);
      setStats({
        totalOrders: filteredSales.length,
        totalItems,
        totalAmount
      });
      
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [selectedDate]);

  // Print single order
  const handlePrintOrder = (order, orderIndex) => {
    const itemsHTML = order.items?.map((item, itemIndex) => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${itemIndex + 1}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.product_name}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center; font-size: 22px; font-weight: bold; color: #C2185B;">${item.quantity}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">
          <input type="checkbox" style="width: 22px; height: 22px;">
        </td>
      </tr>
    `).join('') || '';
    
    const totalItems = order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="${language === 'ar' ? 'rtl' : 'ltr'}">
        <head>
          <meta charset="UTF-8">
          <title>Order - ${order.customer_name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              direction: ${language === 'ar' ? 'rtl' : 'ltr'};
              color: #333;
              max-width: 400px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 15px;
              border-bottom: 3px solid #C2185B;
              padding-bottom: 12px;
            }
            .header img {
              width: 50px;
              height: 50px;
            }
            .header h1 {
              color: #C2185B;
              margin: 5px 0;
              font-size: 18px;
            }
            .header .date {
              font-size: 12px;
              color: #666;
            }
            .customer-box {
              background: linear-gradient(135deg, #C2185B 0%, #E91E63 100%);
              color: white;
              padding: 15px;
              border-radius: 10px;
              margin-bottom: 15px;
              text-align: center;
            }
            .customer-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .order-info {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
              opacity: 0.9;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th {
              background: #f5f5f5;
              color: #333;
              padding: 10px;
              text-align: ${language === 'ar' ? 'right' : 'left'};
              font-size: 13px;
              border-bottom: 2px solid #C2185B;
            }
            th:first-child, th:last-child { text-align: center; }
            th:nth-child(3) { text-align: center; }
            td {
              padding: 12px 10px;
            }
            .total-row {
              background: #f8f9fa;
              font-weight: bold;
              font-size: 16px;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 10px;
              color: #999;
              border-top: 1px solid #eee;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; padding: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" alt="Logo">
            <h1>Qassion Bakeries</h1>
            <div class="date">${formatDate(selectedDate)}</div>
          </div>
          
          <div class="customer-box">
            <div class="customer-name">${order.customer_name}</div>
            <div class="order-info">
              <span>${order.invoice_number}</span>
              <span>${totalItems} items</span>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>Product</th>
                <th style="width: 80px;">Qty</th>
                <th style="width: 60px;">Done</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHTML}
              <tr class="total-row">
                <td colspan="2" style="text-align: ${language === 'ar' ? 'left' : 'right'}; border-top: 2px solid #C2185B;">Total Items:</td>
                <td style="text-align: center; border-top: 2px solid #C2185B; color: #C2185B;">${totalItems}</td>
                <td style="border-top: 2px solid #C2185B;"></td>
              </tr>
            </tbody>
          </table>
          
          <div class="footer">
            Printed: ${new Date().toLocaleString('en-GB')} | Kitchen Copy
          </div>
        </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url);
    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
        URL.revokeObjectURL(url);
      }, 500);
    };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <ChefHat className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'طلبيات اليوم' : "Today's Orders"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {language === 'ar' ? 'قائمة الطلبات للتحضير' : 'Orders list for preparation'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-auto"
              data-testid="date-picker"
            />
          </div>
          <Button variant="outline" onClick={fetchOrders} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'عدد الطلبات' : 'Total Orders'}
                </p>
                <p className="text-2xl font-bold text-primary">{stats.totalOrders}</p>
              </div>
              <ClipboardList className="h-8 w-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'إجمالي القطع' : 'Total Items'}
                </p>
                <p className="text-2xl font-bold text-primary">{stats.totalItems}</p>
              </div>
              <Package className="h-8 w-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'إجمالي المبيعات' : 'Total Sales'}
                </p>
                <p className="text-2xl font-bold text-green-600">£{stats.totalAmount.toFixed(2)}</p>
              </div>
              <span className="text-3xl text-primary/20">£</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print Area - Hidden on screen, shown in print */}
      <div id="print-area" className="hidden">
        <div className="header">
          <img 
            src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" 
            alt="Logo" 
            className="logo"
          />
          <h1>Qassion Bakeries</h1>
          <div className="date">
            {language === 'ar' ? 'طلبيات يوم' : 'Orders for'}: {formatDate(selectedDate)}
          </div>
        </div>
        
        <div className="stats">
          <div className="stat-item">
            <div className="stat-value">{stats.totalOrders}</div>
            <div className="stat-label">{language === 'ar' ? 'طلب' : 'Orders'}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.totalItems}</div>
            <div className="stat-label">{language === 'ar' ? 'قطعة' : 'Items'}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">£{stats.totalAmount.toFixed(2)}</div>
            <div className="stat-label">{language === 'ar' ? 'المجموع' : 'Total'}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>{language === 'ar' ? 'المنتج' : 'Product'}</th>
              <th>{language === 'ar' ? 'الكمية المطلوبة' : 'Quantity'}</th>
            </tr>
          </thead>
          <tbody>
            {aggregatedProducts.map((product, index) => (
              <tr key={product.product_id || index}>
                <td>{index + 1}</td>
                <td>{product.product_name}</td>
                <td className="quantity">{product.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Aggregated Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            {language === 'ar' ? 'قائمة التحضير' : 'Preparation List'}
            <Badge variant="secondary" className="ms-2">
              {aggregatedProducts.length} {language === 'ar' ? 'منتج' : 'products'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : aggregatedProducts.length === 0 ? (
            <div className="text-center py-12">
              <ChefHat className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">
                {language === 'ar' ? 'لا توجد طلبات لهذا اليوم' : 'No orders for this day'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'ar' ? 'اختر تاريخاً آخر أو أضف مبيعات جديدة' : 'Select another date or add new sales'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{language === 'ar' ? 'المنتج' : 'Product'}</TableHead>
                    <TableHead className="text-center">{language === 'ar' ? 'الكمية المطلوبة' : 'Quantity Needed'}</TableHead>
                    <TableHead className="text-center">{language === 'ar' ? 'سعر الوحدة' : 'Unit Price'}</TableHead>
                    <TableHead className="text-center">{language === 'ar' ? 'المجموع' : 'Total'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aggregatedProducts.map((product, index) => (
                    <TableRow key={product.product_id || index} data-testid={`product-row-${index}`}>
                      <TableCell className="font-medium text-muted-foreground">{index + 1}</TableCell>
                      <TableCell className="font-semibold">{product.product_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="text-lg px-4 py-1 bg-primary/10 text-primary hover:bg-primary/20">
                          {product.quantity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground">
                        £{product.unit_price.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center font-semibold text-green-600">
                        £{product.total.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Orders */}
      {orders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              {language === 'ar' ? 'تفاصيل الطلبات' : 'Order Details'}
              <Badge variant="outline" className="ms-2">
                {orders.length} {language === 'ar' ? 'طلب' : 'orders'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'ar' ? 'رقم الفاتورة' : 'Invoice #'}</TableHead>
                    <TableHead>{language === 'ar' ? 'العميل' : 'Customer'}</TableHead>
                    <TableHead>{language === 'ar' ? 'المنتجات' : 'Products'}</TableHead>
                    <TableHead className="text-center">{language === 'ar' ? 'المجموع' : 'Total'}</TableHead>
                    <TableHead className="text-center">{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead className="text-center">{language === 'ar' ? 'طباعة' : 'Print'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order, index) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-sm">{order.invoice_number}</TableCell>
                      <TableCell className="font-semibold">{order.customer_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {order.items?.slice(0, 3).map((item, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {item.product_name} × {item.quantity}
                            </Badge>
                          ))}
                          {order.items?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{order.items.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-semibold">£{order.total?.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={order.payment_status === 'paid' ? 'default' : 'destructive'}>
                          {order.payment_status === 'paid' 
                            ? (language === 'ar' ? 'مدفوع' : 'Paid')
                            : (language === 'ar' ? 'معلق' : 'Pending')
                          }
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePrintOrder(order, index)}
                          data-testid={`print-order-${order.id}`}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
